import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldPlus, Beaker } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ENTITY_TYPES = ["Individual", "Company", "Exchange", "VASP", "Protocol", "Wallet"];

const PRESETS = [
  {
    key: "mixer",
    label: "Tornado Cash (mixer)",
    data: { name: "Tornado Cash", entity_type: "Protocol", jurisdiction: "United States",
      wallet_addresses: "0x8589427373D6D84E98730D7795D8f6f8731FDA16",
      activity_notes: "OFAC-designated coin mixer; anonymity techniques observed.",
      flags: { mixer_used: true, unhosted_wallet: true } },
  },
  {
    key: "ofac-bank",
    label: "Banco Nacional de Cuba",
    data: { name: "Banco Nacional de Cuba", entity_type: "Company", jurisdiction: "Cuba",
      wallet_addresses: "", activity_notes: "Cross-border settlement flagged by a correspondent bank.",
      flags: {} },
  },
  {
    key: "dprk",
    label: "Lazarus Group (DPRK)",
    data: { name: "Lazarus Group", entity_type: "Company", jurisdiction: "North Korea",
      wallet_addresses: "", activity_notes: "State-linked threat actor associated with exchange hacks.",
      flags: {} },
  },
  {
    key: "vasp",
    label: "Coinbase (registered VASP)",
    data: { name: "Coinbase", entity_type: "VASP", jurisdiction: "United States",
      wallet_addresses: "", activity_notes: "Publicly listed, registered exchange with full KYC onboarding.",
      flags: {} },
  },
];

const FLAGS = [
  { key: "mixer_used", label: "Mixer / tumbler usage" },
  { key: "unhosted_wallet", label: "Unhosted (self-custody) wallet" },
  { key: "vasp_unregistered", label: "Unregistered VASP" },
  { key: "stablecoin_issuer_risk", label: "Stablecoin issuer risk" },
];

export default function NewScreening() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    entity_type: "Individual",
    jurisdiction: "",
    sub_tier: "Free",
    wallet_addresses: "",
    activity_notes: "",
  });
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(false);

  const applyPreset = (p) => {
    setForm({
      name: p.data.name,
      entity_type: p.data.entity_type,
      jurisdiction: p.data.jurisdiction,
      sub_tier: p.data.sub_tier || "Free",
      wallet_addresses: p.data.wallet_addresses,
      activity_notes: p.data.activity_notes,
    });
    setFlags(p.data.flags || {});
    toast.info(`Loaded preset: ${p.label}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Entity name is required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        entity_type: form.entity_type,
        jurisdiction: form.jurisdiction,
        sub_tier: form.sub_tier,
        wallet_addresses: form.wallet_addresses
          .split(/[\n,]/)
          .map((s) => s.trim())
          .filter(Boolean),
        activity_notes: form.activity_notes,
        manual_flags: {
          mixer_used: !!flags.mixer_used,
          unhosted_wallet: !!flags.unhosted_wallet,
          vasp_unregistered: !!flags.vasp_unregistered,
          stablecoin_issuer_risk: !!flags.stablecoin_issuer_risk,
        },
      };
      const { data } = await api.post("/screenings", payload);
      toast.success(`Screening complete — ${data.verdict}`);
      navigate(`/cases/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none transition-colors focus:border-blue-500";
  const lbl = "mb-1.5 block font-mono text-xs uppercase tracking-wider text-slate-400";

  return (
    <div className="max-w-3xl space-y-6" data-testid="new-screening-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">
          New Entity Screening
        </h1>
        <p className="mt-1 text-sm text-slate-400">
          Submit an entity to screen against sanctions, PEP, adverse media and crypto risk.
        </p>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">
          <Beaker className="h-3.5 w-3.5" /> Sample presets
        </div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              data-testid={`preset-${p.key}`}
              onClick={() => applyPreset(p)}
              className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-blue-600 hover:text-white"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={submit} className="space-y-5 rounded-xl border border-slate-800 bg-slate-900/60 p-6">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={lbl}>Entity name *</label>
            <input
              data-testid="entity-name-input"
              className={input}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Full legal name / entity name"
            />
          </div>
          <div>
            <label className={lbl}>Entity type</label>
            <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
              <SelectTrigger data-testid="entity-type-select" className={input}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_TYPES.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`entity-type-${t}`}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={lbl}>Jurisdiction / country</label>
            <input
              data-testid="jurisdiction-input"
              className={input}
              value={form.jurisdiction}
              onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
              placeholder="e.g. Cayman Islands"
            />
          </div>
          <div>
            <label className={lbl}>Subscription tier</label>
            <Select value={form.sub_tier} onValueChange={(v) => setForm({ ...form, sub_tier: v })}>
              <SelectTrigger data-testid="sub-tier-select" className={input}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Free" data-testid="sub-tier-Free">Free</SelectItem>
                <SelectItem value="Pro" data-testid="sub-tier-Pro">Pro</SelectItem>
                <SelectItem value="Enterprise" data-testid="sub-tier-Enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <label className={lbl}>Wallet addresses (comma or newline separated)</label>
          <textarea
            data-testid="wallet-addresses-input"
            rows={2}
            className={`${input} font-mono text-xs`}
            value={form.wallet_addresses}
            onChange={(e) => setForm({ ...form, wallet_addresses: e.target.value })}
            placeholder="0x… , bc1…"
          />
        </div>

        <div>
          <label className={lbl}>Activity notes</label>
          <textarea
            data-testid="activity-notes-input"
            rows={3}
            className={input}
            value={form.activity_notes}
            onChange={(e) => setForm({ ...form, activity_notes: e.target.value })}
            placeholder="Context, transaction patterns, source of funds…"
          />
        </div>

        <div>
          <label className={lbl}>Crypto risk flags (manual input)</label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {FLAGS.map((f) => (
              <label
                key={f.key}
                data-testid={`flag-${f.key}`}
                className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-300 transition-colors hover:border-slate-600"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-blue-500"
                  checked={!!flags[f.key]}
                  onChange={(e) => setFlags({ ...flags, [f.key]: e.target.checked })}
                />
                {f.label}
              </label>
            ))}
          </div>
        </div>

        <button
          data-testid="entity-intake-submit-btn"
          type="submit"
          disabled={loading}
          className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldPlus className="h-4 w-4" />}
          {loading ? "Screening…" : "Run Screening"}
        </button>
      </form>
    </div>
  );
}
